import React, { useEffect, useState } from 'react';
import { useHUser, UserFormData } from '../hooks/HUsers';
import { LoadingSpinner } from '../../utils/LoadingSpinner';
import GUsersTable from '../../tables/GUsersTable';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import UserForm from '../../dialogs/UserForm';
import { User } from '../interfaces/IUsers';

export default function UsersTab() {
    const {
        users,
        setFormData,
        loadUsers,
        loadAllObjects,
        roles,
        fetchRoles,
        fetchRolesDesks,
        
        update_user,
        loading,
    } = useHUser();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [userFormRenderKey, setUserFormRenderKey] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [editRow, setEditRow] = useState<number | null>(null);
    const [editValues, setEditValues] = useState<{ [key: string]: string }>({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

    useEffect(() => {
        void loadUsers();
        void loadAllObjects();
        void fetchRoles();
        void fetchRolesDesks();
    }, [loadUsers, loadAllObjects, fetchRoles, fetchRolesDesks]);

    const handleUserSubmit = async (data: {
        firstName: string;
        lastName: string;
        email: string;
        company: string;
        companyId: string;
        role: string;
    }) => {
        try {
            console.log('[UsersTab] handleUserSubmit called with', data);
            console.trace('[UsersTab] handleUserSubmit trace start');

            // Resolve user id early (prefer editingUser, otherwise lookup by email)
            const matchedEarly = editingUser ?? users.find((u) => {
                const uemail = (u.email || u.user || '').toString().toLowerCase();
                return uemail === (data.email || '').toString().toLowerCase();
            });
            const resolvedUserId = matchedEarly?.id ?? matchedEarly?.user_id ?? matchedEarly?.userId ?? null;

            const userPayload: UserFormData & { user_id?: any } = {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phoneNumber: '',
                company: data.company,
                companyId: data.companyId,
                user_id: resolvedUserId,
            };

            console.log('[UsersTab] built userPayload', userPayload);
            try {
                setFormData(userPayload);

                // Resolve user_id: prefer editingUser, fallback to lookup by email in users list
                const matched = editingUser ?? users.find((u) => {
                    const uemail = (u.email || u.user || '').toString().toLowerCase();
                    return uemail === userPayload.email.toString().toLowerCase();
                });
                const user_id = matched?.id ?? matched?.user_id ?? matched?.userId ?? null;

                if (!user_id) {
                    console.error('[UsersTab] Missing user_id for update; aborting. matched:', matched, 'payload:', userPayload);
                    throw new Error('Missing user_id for update');
                }

                // Map the submitted role string to a role id from `roles` (if available)
                const selectedRole = roles?.find((r: any) => {
                    const roleName = (r.role ?? r.role_name ?? '').toString().toLowerCase();
                    return roleName === (data.role ?? '').toString().toLowerCase();
                });

                // Normalize role id to a number or string usable by backend
                let roleId: any = selectedRole?.id ?? matched?.role_id ?? 0;
                if (typeof roleId === 'bigint') roleId = roleId.toString();

                console.log('[UsersTab] calling update_user with payload', { ...userPayload, user_id, role_id: roleId });
                const res = await update_user({
                    user_id,
                    firstName: userPayload.firstName,
                    lastName: userPayload.lastName,
                    phoneNumber: userPayload.phoneNumber,
                    companyId: userPayload.companyId,
                    role_id: roleId,
                });
                console.log('[UsersTab] update_user resolved successfully', res);
                setSnackbarSeverity('success');
                setSnackbarMessage(res?.message || 'User updated successfully.');
                setSnackbarOpen(true);
            } catch (err) {
                console.error('[UsersTab] error during user update', err);
                setSnackbarSeverity('error');
                setSnackbarMessage(err?.message || String(err) || 'Failed to update user');
                setSnackbarOpen(true);
                throw err;
            }
            setDialogOpen(false);
            setEditingUser(null);
            setFormData({ firstName: '', lastName: '', email: '', phoneNumber: '', company: '', companyId: '' });
        } catch (error) {
            console.error('Failed to submit user:', error);
            setDialogOpen(false);
        }
    };

    const openAddUserDialog = () => {
        setEditingUser(null);
        setDialogOpen(true);
        setUserFormRenderKey((prev) => prev + 1);
    };

    const openEditUserDialog = (user: User) => {
        setEditingUser(user);
        setDialogOpen(true);
        setUserFormRenderKey((prev) => prev + 1);
    };

    const displayedUsers = users.filter((user) => {
        const query = searchInput.trim().toLowerCase();
        if (!query) return true;

        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
        const displayName = (user.display_name || '').toLowerCase();
        const email = (user.email || user.user || '').toLowerCase();

        return fullName.includes(query) || displayName.includes(query) || email.includes(query);
    });

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(event.target.value);
    };

    const handleDeleteUser = async (user: any) => {
        try {
            await loadUsers();
        } catch (err) {
            console.error('Failed to delete user', err);
        }
    };

    return (
        <div className='standard-div'>
            <div className='standard-div-flex'>
                <h1>Users</h1>

                <div className='header-flex'>
                    <TextField
                        size='small'
                        placeholder='Search users by name or email...'
                        value={searchInput}
                        onChange={handleSearchChange}
                        sx={{ marginRight: 1, minWidth: 300 }}
                    />
                    <Button variant='contained' color='primary' onClick={openAddUserDialog}>
                        Add User
                    </Button>
                </div>

                <UserForm
                    key={`user-form-${userFormRenderKey}`}
                    open={dialogOpen}
                    onClose={() => {
                        setDialogOpen(false);
                        setEditingUser(null);
                    }}
                    onSubmit={handleUserSubmit}
                    title={editingUser ? 'Edit User' : 'Add User'}
                    submitText={editingUser ? 'Update User' : 'Invite User'}
                    initialValues={editingUser ? {
                        firstName: (editingUser.first_name || '').toString(),
                        lastName: (editingUser.last_name || '').toString(),
                        email: (editingUser.email || editingUser.user || '').toString(),
                        company: (editingUser.company_name || editingUser.company || '').toString(),
                        companyId: String(editingUser.company_id ?? editingUser.companyId ?? ''),
                        role: (editingUser.role_name || editingUser.role || '').toString(),
                    } : undefined}
                />

                {loading ? (
                    <LoadingSpinner text='Loading users...' />
                ) : displayedUsers.length === 0 ? (
                    <LoadingSpinner text='No users found' />
                ) : (
                    <GUsersTable
                        users={displayedUsers}
                        editRow={editRow}
                        editValues={editValues}
                        setEditRow={setEditRow}
                        setEditValues={setEditValues}
                        handleEditSave={async () => { setEditRow(null); }}
                        handleDelete={handleDeleteUser}
                        onEditUser={openEditUserDialog}
                    />
                )}
            </div>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}
